import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { schoolsApi, reviewsApi } from "../api";
import {
  BookOpen, Users, BarChart2, Bell, MessageSquare, Shield,
  CheckCircle, Star, ArrowRight, Menu, X, Brain, FileText,
  Trophy, GraduationCap, Sparkles, Calendar, CreditCard,
  Zap, Building2, TrendingUp, Clock, Globe, Award,
  ChevronDown, Play, Lock, Smartphone,
  Mail, Phone, MapPin, Send, ExternalLink, Briefcase, Heart
} from "lucide-react";

/* ─── Animation ───────────────────────────────────────────────── */
const E = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08, ease: E } })
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.09 } } };

function FadeIn({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-72px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 22 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: E }} className={className}>
      {children}
    </motion.div>
  );
}

/* Count-up on scroll */
function CountUp({ to, suffix = "", duration = 1.6 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start; let raf;
    const tick = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / (duration * 1000), 1);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return <span ref={ref}>{n.toLocaleString()}{suffix}</span>;
}

/* ─── Data ────────────────────────────────────────────────────── */
const NAV_LINKS = [
  { label: "Features", href: "#features" },
  { label: "AI Tools", href: "#ai" },
  { label: "Pricing", href: "#pricing" },
  { label: "Testimonials", href: "#testimonials" },
];

const STATS_FALLBACK = [
  { value: "500+", label: "Schools & Coaching Centers" },
  { value: "50K+", label: "Active Students" },
  { value: "2,000+", label: "Teachers Empowered" },
  { value: "98%", label: "Satisfaction Rate" },
];

const ROLE_TABS = [
  { key: "admin", icon: Building2, label: "Principal / Admin", accent: "#E8690A" },
  { key: "teacher", icon: GraduationCap, label: "Teachers", accent: "#4F46E5" },
  { key: "student", icon: Trophy, label: "Students", accent: "#7C3AED" },
  { key: "parent", icon: Users, label: "Parents", accent: "#0891B2" },
];

const ROLE_FEATURES = {
  admin: [
    { icon: Users, title: "Staff & Student Directory", desc: "Unified roster management with role-based access for every staff member." },
    { icon: BarChart2, title: "Live Analytics Dashboard", desc: "Real-time KPIs — attendance rates, fee collection, and academic performance." },
    { icon: CreditCard, title: "Fee Management", desc: "Flexible fee structures, auto-receipts, and overdue payment tracking." },
    { icon: Calendar, title: "Timetable Builder", desc: "Drag-and-drop class scheduling with conflict detection." },
    { icon: Bell, title: "Push Announcements", desc: "Broadcast to all students, teachers, and parents instantly." },
    { icon: Shield, title: "Role-Based Permissions", desc: "Granular control over who sees and does what in your school." },
  ],
  teacher: [
    { icon: FileText, title: "AI Exam Paper Maker", desc: "Generate full exam papers from your question bank or uploaded PDFs in seconds." },
    { icon: Brain, title: "AI Test Builder", desc: "Create MCQs, short answers, and fill-in-the-blanks with AI difficulty tuning." },
    { icon: BookOpen, title: "Homework & Assignments", desc: "Assign, track submissions, and give digital feedback — all in one place." },
    { icon: BarChart2, title: "Digital Gradebook", desc: "Record marks, compute grades, and share report cards with one click." },
    { icon: MessageSquare, title: "Real-Time Chat", desc: "Direct messaging with students and parents with read receipts." },
    { icon: Clock, title: "Attendance Tracker", desc: "One-tap attendance marking per class, with instant parent notifications." },
  ],
  student: [
    { icon: Brain, title: "AI Study Planner", desc: "Personalized weekly study schedules generated by AI based on syllabus and exams." },
    { icon: Sparkles, title: "AI Practice Quizzes", desc: "Topic-wise quizzes auto-generated to test understanding after each study slot." },
    { icon: Trophy, title: "Gamified Learning Tiers", desc: "Progress from Bronze → Silver → Gold → Platinum → Crown with earned badges." },
    { icon: TrendingUp, title: "Performance Reports", desc: "Detailed report cards, subject-wise trends, and improvement suggestions." },
    { icon: Calendar, title: "Exam Calendar", desc: "Never miss a test with smart reminders synced to your school's schedule." },
    { icon: MessageSquare, title: "Chat with Teachers", desc: "Raise doubts and get answers from subject teachers in real time." },
  ],
  parent: [
    { icon: BarChart2, title: "Live Progress Tracking", desc: "See your child's marks, attendance, and assignment status anytime." },
    { icon: Bell, title: "Instant Alerts", desc: "Push notifications for absences, new fees, and school announcements." },
    { icon: CreditCard, title: "Fee Visibility", desc: "View complete payment history and pending amounts in a tap." },
    { icon: BookOpen, title: "Assignment Overview", desc: "Know exactly what homework is pending and what's been submitted." },
    { icon: MessageSquare, title: "School Communication", desc: "Direct line to class teachers and school administration." },
    { icon: Calendar, title: "Leave Requests", desc: "Apply for leaves and track approval status without calling the school." },
  ],
};

const AI_FEATURES = [
  {
    icon: FileText,
    title: "AI Exam Paper Builder",
    desc: "Upload a syllabus PDF or pick from your question bank. Get a balanced, print-ready exam paper with MCQs, short answers, and long answers — in under 60 seconds.",
    accent: "#E8690A",
    tag: "For Teachers",
  },
  {
    icon: Brain,
    title: "AI Study Planner",
    desc: "EduPulseAI analyzes each student's syllabus, upcoming exams, and weak topics to generate a personalized daily study plan — complete with timers and built-in quizzes.",
    accent: "#4F46E5",
    tag: "For Students",
  },
  {
    icon: Sparkles,
    title: "AI Practice Quiz Engine",
    desc: "Auto-generate topic-specific quizzes after every study session. Questions adapt to the student's performance, ensuring the right level of challenge every time.",
    accent: "#7C3AED",
    tag: "For Students",
  },
];

const PLANS = [
  {
    name: "Free",
    monthlyPrice: 0,
    annualTotal: 0,
    annualEffective: 0,
    annualSaving: 0,
    desc: "Lead-gen tier — try before you commit",
    color: "#5A6278",
    limits: { students: "50", staff: "5", storage: "1 GB", support: "Email · 5 days" },
    highlights: [
      "50 students · 5 staff accounts",
      "Basic attendance (manual only)",
      "Simple fee ledger (no receipts)",
      "School announcements — 30/month",
      "Student portal — read-only access",
      "1 GB cloud storage",
    ],
    notIncluded: ["Parent portal", "Chat", "All AI features", "Custom branding"],
    cta: "Start Free",
    highlighted: false,
    badge: null,
  },
  {
    name: "Basic",
    monthlyPrice: 2999,
    annualTotal: 29999,
    annualEffective: 2500,
    annualSaving: 5989,
    desc: "Small coaching centers up to 150 students",
    color: "#4F46E5",
    limits: { students: "150", staff: "10", storage: "10 GB", support: "Chat · 24 hrs" },
    highlights: [
      "150 students · 10 staff accounts",
      "Full attendance + parent auto-alerts",
      "Fee management + digital receipts",
      "Timetable builder",
      "Parent portal + parent app",
      "Chat: teachers ↔ students & parents",
      "Homework & assignments tracker",
      "Basic gradebook & report cards",
      "10 GB cloud storage",
    ],
    notIncluded: ["AI features", "Custom branding", "Advanced analytics"],
    cta: "Start Free Trial",
    highlighted: false,
    badge: null,
  },
  {
    name: "Standard",
    monthlyPrice: 6999,
    annualTotal: 69999,
    annualEffective: 5833,
    annualSaving: 13989,
    desc: "Growing schools — 150 to 600 students",
    color: "#E8690A",
    limits: { students: "600", staff: "40", storage: "50 GB", support: "Priority · 4 hrs" },
    highlights: [
      "600 students · 40 staff accounts",
      "Everything in Basic",
      "AI Exam Paper Builder — 100 papers/mo",
      "AI Study Planner for all students",
      "AI Practice Quiz Engine — unlimited",
      "Advanced analytics + CSV/Excel export",
      "Custom branding: logo, colors, school website",
      "Push notifications — unlimited",
      "GST invoicing + payment gateway integration",
      "50 GB cloud storage",
    ],
    notIncluded: ["Multi-branch", "White-label", "API access"],
    cta: "Start 14-Day Trial",
    highlighted: true,
    badge: "Most Popular",
  },
  {
    name: "Professional",
    monthlyPrice: 14999,
    annualTotal: 149999,
    annualEffective: 12500,
    annualSaving: 29989,
    desc: "Established schools — up to 2,000 students",
    color: "#7C3AED",
    limits: { students: "2,000", staff: "120", storage: "100 GB", support: "Priority · 2 hrs" },
    highlights: [
      "2,000 students · 120 staff accounts",
      "Everything in Standard",
      "AI Paper Builder — unlimited papers",
      "AI Test Builder — unlimited",
      "Multi-branch management (up to 3 branches)",
      "Custom school website + domain",
      "Advanced fee: payment gateway + GST reports",
      "Onboarding assistance included",
      "100 GB cloud storage",
    ],
    notIncluded: ["White-label", "REST API", "Custom integrations"],
    cta: "Start 14-Day Trial",
    highlighted: false,
    badge: "Best Value",
  },
  {
    name: "Enterprise",
    monthlyPrice: 29999,
    annualTotal: null,
    annualEffective: null,
    annualSaving: null,
    desc: "School chains & large institutions",
    color: "#0891B2",
    limits: { students: "Unlimited", staff: "Unlimited", storage: "200 GB+", support: "SLA · 1 hr" },
    highlights: [
      "Unlimited students & staff",
      "Everything in Professional",
      "Unlimited AI across all features",
      "Unlimited branches & campuses",
      "White-label platform — your brand",
      "REST API + webhooks + custom integrations",
      "Dedicated customer success manager",
      "SLA-backed 1-hour support response",
      "Custom data residency & security audit",
      "200 GB+ cloud storage",
    ],
    notIncluded: [],
    cta: "Contact Sales",
    highlighted: false,
    badge: null,
  },
];

/* Add-ons */
const ADDONS = [
  { icon: MessageSquare, title: "SMS Notification Pack", price: "₹999/mo", desc: "5,000 SMS credits/month for parents who don't use the app. Top-up packs available." },
  { icon: Users, title: "Extra Student Pack", price: "₹25/student/mo", desc: "Over your plan limit? Add students at ₹25 each without upgrading your entire plan." },
  { icon: Zap, title: "Priority Onboarding", price: "₹9,999 one-time", desc: "Dedicated 3-day setup session with our team — data migration, staff training, go-live support." },
  { icon: Globe, title: "Custom Domain + SSL", price: "₹2,999/yr", desc: "Host your school portal on your own domain (e.g., portal.yourschool.in) with full SSL." },
];

/* Feature comparison rows — 5 plans */
const COMPARISON_ROWS = [
  { category: "Core Limits" },
  { label: "Students", values: ["50", "150", "600", "2,000", "Unlimited"] },
  { label: "Staff accounts", values: ["5", "10", "40", "120", "Unlimited"] },
  { label: "Cloud storage", values: ["1 GB", "10 GB", "50 GB", "100 GB", "200 GB+"] },
  { label: "Branches", values: ["1", "1", "1", "3", "Unlimited"] },

  { category: "Attendance & Communication" },
  { label: "Attendance tracking", values: [true, true, true, true, true] },
  { label: "Parent auto-alerts (push)", values: [false, true, true, true, true] },
  { label: "SMS alerts", values: [false, "Add-on", "Add-on", "Add-on", "Included"] },
  { label: "In-app chat (all roles)", values: [false, true, true, true, true] },
  { label: "Push announcements", values: ["30/mo", "Unlimited", "Unlimited", "Unlimited", "Unlimited"] },
  { label: "Parent portal + app", values: [false, true, true, true, true] },

  { category: "Academics & Admin" },
  { label: "Timetable builder", values: [false, true, true, true, true] },
  { label: "Homework & assignments", values: [false, true, true, true, true] },
  { label: "Digital gradebook & reports", values: [false, true, true, true, true] },
  { label: "Fee management", values: ["Basic", "Full", "Full + GST", "Full + GST + Gateway", "Full + GST + Gateway"] },
  { label: "Leave management", values: [false, true, true, true, true] },
  { label: "Multi-branch management", values: [false, false, false, "3 branches", "Unlimited"] },

  { category: "AI Features" },
  { label: "AI Exam Paper Builder", values: [false, false, "100/mo", "Unlimited", "Unlimited"] },
  { label: "AI Study Planner", values: [false, false, true, true, true] },
  { label: "AI Practice Quiz Engine", values: [false, false, true, true, true] },
  { label: "AI Test Builder", values: [false, false, false, true, true] },

  { category: "Analytics & Branding" },
  { label: "Analytics dashboard", values: ["Basic", "Standard", "Advanced", "Advanced", "Custom"] },
  { label: "CSV / Excel exports", values: [false, false, true, true, true] },
  { label: "Custom school branding", values: [false, false, true, true, true] },
  { label: "School website builder", values: [false, false, true, true, true] },
  { label: "Custom domain", values: [false, false, "Add-on", "Included", "Included"] },
  { label: "White-label platform", values: [false, false, false, false, true] },

  { category: "Developer & Integrations" },
  { label: "REST API access", values: [false, false, false, false, true] },
  { label: "Webhooks", values: [false, false, false, false, true] },
  { label: "Custom integrations", values: [false, false, false, false, true] },

  { category: "Support" },
  { label: "Support channel", values: ["Email", "Email + Chat", "Priority", "Priority", "SLA"] },
  { label: "Response time", values: ["5 days", "24 hrs", "4 hrs", "2 hrs", "1 hr"] },
  { label: "Onboarding assistance", values: [false, false, false, true, true] },
  { label: "Dedicated success manager", values: [false, false, false, false, true] },
  { label: "Custom security audit", values: [false, false, false, false, true] },
];

const PRICING_FAQ = [
  {
    q: "Is there a free trial on paid plans?",
    a: "Yes — Basic, Standard, and Professional come with a 14-day free trial, no credit card required. You get full access to every feature in your chosen plan before paying a rupee.",
  },
  {
    q: "Why is the annual plan so much cheaper?",
    a: "Annual billing saves us collection costs, churn, and cash-flow uncertainty — so we pass the saving to you. On Standard, you save ₹13,989 per year (2 months free). On Professional, you save ₹29,989. The annual price is locked in and won't change even if we raise monthly rates.",
  },
  {
    q: "Is there a one-time setup fee?",
    a: "No mandatory setup fee. However, we offer an optional Priority Onboarding add-on (₹9,999 one-time) where our team handles your full data migration, staff training, and go-live over 3 dedicated days. Most schools on Professional and Enterprise take this.",
  },
  {
    q: "What if my school has more students than my plan limit?",
    a: "You can add students at ₹25/student/month without upgrading your full plan. For example, a 700-student school on Standard (600 limit) adds 100 extra students for ₹2,500/month — total ₹9,499/month — still cheaper than Professional.",
  },
  {
    q: "What payment methods do you accept?",
    a: "UPI, all major Credit and Debit cards, Net Banking, and NEFT/RTGS for annual plans. All payments are processed securely via Razorpay. You'll receive a GST-compliant invoice immediately after every payment.",
  },
  {
    q: "Are the prices inclusive of GST?",
    a: "Listed prices are exclusive of 18% GST. So Standard at ₹6,999/month becomes ₹8,259/month including GST. A proper GST invoice is generated for every payment — you can claim input tax credit if your school is GST-registered.",
  },
  {
    q: "Do you offer discounts for NGOs or government schools?",
    a: "Yes — up to 40% off for verified NGOs, government-aided schools, and schools in Tier-3 cities or rural areas. We also have a special Founder Pricing program for our first 50 schools. Write to sales@edupulseai.co.in.",
  },
  {
    q: "What happens to our data if we cancel?",
    a: "Your data is retained for 30 days after cancellation. During this window you can export everything in CSV/Excel. After 30 days it is securely and permanently deleted per our Privacy Policy. We never hold your data hostage.",
  },
];

const TESTIMONIALS = [
  {
    quote: "EduPulseAI transformed how we run our 800-student school. The AI paper maker alone saves our teachers 5+ hours every exam cycle — it's remarkable.",
    name: "Dr. Rajesh Sharma",
    role: "Principal, Delhi Public Academy",
    avatar: "RS",
    rating: 5,
  },
  {
    quote: "Parents get instant alerts the moment attendance is marked. We've reduced pending fee collections by 40% in just three months. The ROI is undeniable.",
    name: "Mrs. Priya Nair",
    role: "Director, Sunshine Coaching Center",
    avatar: "PN",
    rating: 5,
  },
  {
    quote: "My students love the gamified study planner. Watching their tier progress from Bronze to Gold keeps them consistently motivated. EduPulseAI actually makes studying fun.",
    name: "Mr. Amit Verma",
    role: "Senior Faculty, IIT Foundation Classes",
    avatar: "AV",
    rating: 5,
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Set Up Your School",
    desc: "Add your school details, class structure, and subjects in minutes using our guided setup wizard. No technical expertise needed.",
    icon: Building2,
  },
  {
    step: "02",
    title: "Invite Your Team",
    desc: "Send one-click invites to teachers, staff, students, and parents. Everyone gets their own tailored portal instantly.",
    icon: Users,
  },
  {
    step: "03",
    title: "Watch It Transform",
    desc: "AI kicks in, parents stay informed, teachers save hours, and students perform better. Your school runs on autopilot.",
    icon: Sparkles,
  },
];

/* ─── Dashboard Mockup ────────────────────────────────────────── */
function DashboardMockup() {
  return (
    <div className="relative w-full max-w-[520px] mx-auto">
      {/* Ambient glow */}
      <div
        className="absolute inset-0 rounded-3xl blur-3xl opacity-30"
        style={{ background: "radial-gradient(ellipse at 50% 60%, #E8690A 0%, #4F46E5 60%, transparent 80%)" }}
      />

      {/* Browser chrome */}
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="relative rounded-2xl overflow-hidden border border-white/10"
        style={{ background: "linear-gradient(160deg, #0D1424 0%, #111D35 50%, #0E1628 100%)", boxShadow: "0 32px 80px rgba(0,0,0,0.5)" }}
      >
        {/* Window bar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5" style={{ background: "rgba(255,255,255,0.03)" }}>
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ background: "#FF5F57" }} />
            <div className="w-3 h-3 rounded-full" style={{ background: "#FFBD2E" }} />
            <div className="w-3 h-3 rounded-full" style={{ background: "#28C840" }} />
          </div>
          <div className="flex-1 mx-3 h-5 rounded-md flex items-center px-3 gap-1.5" style={{ background: "rgba(255,255,255,0.05)" }}>
            <Lock className="w-2.5 h-2.5 text-white/30" />
            <span className="text-[9px] text-white/30 font-mono">edupulseai.co.in/dashboard</span>
          </div>
        </div>

        {/* App layout */}
        <div className="flex" style={{ height: 260 }}>
          {/* Sidebar */}
          <div className="w-11 flex flex-col items-center py-3 gap-2.5 border-r border-white/5" style={{ background: "rgba(10,15,30,0.8)" }}>
            <div className="w-7 h-7 rounded-lg overflow-hidden mb-1 flex-shrink-0">
              <img src="/logo192.png" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            {[BarChart2, Users, BookOpen, Bell, MessageSquare, Shield].map((Icon, i) => (
              <div
                key={i}
                className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
                style={{ background: i === 0 ? "rgba(232,105,10,0.2)" : "transparent" }}
              >
                <Icon className="w-3.5 h-3.5" style={{ color: i === 0 ? "#E8690A" : "rgba(255,255,255,0.25)" }} />
              </div>
            ))}
          </div>

          {/* Main content */}
          <div className="flex-1 p-3 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-white text-[11px] font-sora font-semibold">Good morning, Principal 👋</p>
                <p className="text-[8px]" style={{ color: "rgba(255,255,255,0.3)" }}>Wednesday, 28 May 2025</p>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: "rgba(255,255,255,0.06)" }}>
                  <Bell className="w-2.5 h-2.5" style={{ color: "rgba(255,255,255,0.4)" }} />
                </div>
                <div className="w-6 h-6 rounded-full gradient-brand flex items-center justify-center">
                  <span className="text-[8px] font-bold text-white">A</span>
                </div>
              </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-4 gap-1.5 mb-2.5">
              {[
                { label: "Students", value: "842", color: "#E8690A" },
                { label: "Attendance", value: "94%", color: "#4F46E5" },
                { label: "Fee Due", value: "₹84K", color: "#0891B2" },
                { label: "Papers", value: "12", color: "#7C3AED" },
              ].map((k, i) => (
                <div key={i} className="rounded-lg p-1.5" style={{ background: `${k.color}12`, border: `1px solid ${k.color}22` }}>
                  <p className="font-sora font-semibold text-[10px]" style={{ color: k.color }}>{k.value}</p>
                  <p className="text-[7px]" style={{ color: "rgba(255,255,255,0.35)" }}>{k.label}</p>
                </div>
              ))}
            </div>

            {/* Chart */}
            <div className="rounded-xl p-2 mb-2" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
              <div className="flex justify-between items-center mb-2">
                <p className="text-[8px] font-sora" style={{ color: "rgba(255,255,255,0.5)" }}>Weekly Attendance</p>
                <div className="flex gap-1">
                  {["W", "M", "Y"].map((t, i) => (
                    <span key={i} className="text-[6px] px-1.5 py-0.5 rounded-sm font-medium" style={{ background: i === 0 ? "rgba(232,105,10,0.25)" : "transparent", color: i === 0 ? "#E8690A" : "rgba(255,255,255,0.25)" }}>{t}</span>
                  ))}
                </div>
              </div>
              <div className="flex items-end gap-1" style={{ height: 40 }}>
                {[72, 88, 94, 86, 97, 91, 89].map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t-sm"
                    style={{
                      height: `${h}%`,
                      background: i === 4 ? "linear-gradient(to top, #E8690A, #F59E0B)" : "rgba(79,70,229,0.35)",
                      transition: "all 0.3s"
                    }}
                  />
                ))}
              </div>
              <div className="flex justify-between mt-1">
                {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                  <span key={i} className="flex-1 text-center text-[6px]" style={{ color: "rgba(255,255,255,0.2)" }}>{d}</span>
                ))}
              </div>
            </div>

            {/* Activity list */}
            <div className="space-y-1">
              {[
                { text: "Class 10A — 46/48 present", dot: "#22c55e" },
                { text: "Fee collected: ₹12,500 · 3 overdue", dot: "#E8690A" },
                { text: "AI paper ready: Class 12 Physics", dot: "#4F46E5" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: item.dot }} />
                  <span className="text-[7px]" style={{ color: "rgba(255,255,255,0.35)" }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Floating badge — top right */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8, x: 20 }}
        animate={{ opacity: 1, scale: 1, x: 0 }}
        transition={{ delay: 0.9 }}
        className="absolute -top-3 -right-6 bg-white rounded-2xl shadow-modal px-3 py-2 flex items-center gap-2 z-10"
        style={{ animation: "float1 3s ease-in-out infinite" }}
      >
        <div className="w-8 h-8 rounded-xl gradient-brand flex items-center justify-center flex-shrink-0">
          <Brain className="w-4 h-4 text-white" />
        </div>
        <div>
          <p className="text-[10px] font-sora font-semibold text-navy-800">AI Paper Ready</p>
          <p className="text-[8px] text-navy-400">30 Qs · Class 10 Math</p>
        </div>
      </motion.div>

      {/* Floating badge — bottom left */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8, x: -20 }}
        animate={{ opacity: 1, scale: 1, x: 0 }}
        transition={{ delay: 1.1 }}
        className="absolute -bottom-3 -left-6 bg-white rounded-2xl shadow-modal px-3 py-2 flex items-center gap-2 z-10"
        style={{ animation: "float2 4s ease-in-out infinite" }}
      >
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#4F46E5,#7C3AED)" }}>
          <Trophy className="w-4 h-4 text-white" />
        </div>
        <div>
          <p className="text-[10px] font-sora font-semibold text-navy-800">Rahul reached Gold!</p>
          <p className="text-[8px] text-navy-400">Leaderboard updated</p>
        </div>
      </motion.div>
    </div>
  );
}

/* ─── Navbar ──────────────────────────────────────────────────── */
function Navbar({ scrolled, onNav, mobileOpen, setMobileOpen, navigate }) {
  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? "rgba(255,255,255,0.92)" : "transparent",
          backdropFilter: scrolled ? "blur(20px) saturate(180%)" : "none",
          borderBottom: scrolled ? "1px solid rgba(0,0,0,0.07)" : "1px solid transparent",
          boxShadow: scrolled ? "0 1px 24px rgba(0,0,0,0.06)" : "none",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <div className="w-9 h-9 rounded-xl overflow-hidden shrink-0" style={{ background: "#fff", padding: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.3)" }}>
              <img src="/logo192.png" alt="EduPulseAI" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8, display: "block" }} />
            </div>
            <span className="font-sora font-bold text-lg" style={{ color: scrolled ? "#0D1B36" : "#fff" }}>
              EduPulse<span className="text-gradient-brand">AI</span>
            </span>
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.label}
                onClick={() => onNav(link.href)}
                className="px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 hover:bg-white/10"
                style={{ color: scrolled ? "#1E3A5F" : "rgba(255,255,255,0.8)" }}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA buttons */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => navigate("/login")}
              className="px-4 py-2 rounded-xl text-sm font-semibold border transition-all duration-200 hover:bg-white/10"
              style={{ color: scrolled ? "#1E3A5F" : "#fff", borderColor: scrolled ? "rgba(30,58,95,0.2)" : "rgba(255,255,255,0.25)" }}
            >
              Login
            </button>
            <button
              onClick={() => navigate("/login")}
              className="px-5 py-2 rounded-xl text-sm font-semibold text-white gradient-brand shadow-glow-brand transition-all duration-200 hover:opacity-90 hover:shadow-lg"
            >
              Get Started Free
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-xl transition-all"
            style={{ color: scrolled ? "#0D1B36" : "#fff" }}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="fixed top-16 left-0 right-0 z-40 bg-white shadow-modal border-b border-navy-100 px-4 py-4 md:hidden"
          >
            <nav className="flex flex-col gap-1 mb-4">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.label}
                  onClick={() => onNav(link.href)}
                  className="px-4 py-3 rounded-xl text-sm font-medium text-navy-700 hover:bg-navy-50 text-left transition-all"
                >
                  {link.label}
                </button>
              ))}
            </nav>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate("/login")}
                className="w-full px-4 py-3 rounded-xl text-sm font-semibold text-navy-700 border border-navy-200 hover:bg-navy-50 transition-all"
              >
                Login
              </button>
              <button
                onClick={() => navigate("/login")}
                className="w-full px-4 py-3 rounded-xl text-sm font-semibold text-white gradient-brand shadow-glow-brand transition-all"
              >
                Get Started Free
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ─── Hero Section ────────────────────────────────────────────── */
function HeroSection({ navigate }) {
  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden"
      style={{ background: "linear-gradient(135deg,#06091A 0%,#0A1428 45%,#0D1B36 100%)" }}>
      {/* Grid texture */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: "linear-gradient(rgba(255,255,255,0.035) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.035) 1px,transparent 1px)",
        backgroundSize: "60px 60px",
      }} />
      {/* Glow orbs */}
      <div className="absolute pointer-events-none" style={{ top: "18%", left: "8%", width: 480, height: 480, borderRadius: "50%", background: "radial-gradient(circle,rgba(232,105,10,0.16) 0%,transparent 70%)" }} />
      <div className="absolute pointer-events-none" style={{ top: "-5%", right: "12%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle,rgba(79,70,229,0.16) 0%,transparent 70%)" }} />
      <div className="absolute pointer-events-none" style={{ bottom: "20%", right: "5%", width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle,rgba(124,58,237,0.12) 0%,transparent 70%)" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-16 flex-1 flex items-center w-full">
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-10 items-center">
          {/* Left */}
          <div>
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full mb-7"
              style={{ background: "rgba(232,105,10,0.12)", border: "1px solid rgba(232,105,10,0.4)" }}>
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
              <span className="text-xs font-semibold text-orange-400 font-sora tracking-wide">AI-Powered School Management</span>
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, delay: 0.1 }}
              className="font-sora font-extrabold text-white mb-6" style={{ fontSize: "clamp(2.6rem,5.5vw,4rem)", lineHeight: 1.08, letterSpacing: "-0.02em" }}>
              Run your school<br />
              <span style={{ background: "linear-gradient(90deg,#F59E0B,#E8690A)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                smarter, not harder.
              </span>
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.22 }}
              className="text-[17px] leading-relaxed mb-9 max-w-lg" style={{ color: "rgba(255,255,255,0.58)" }}>
              The complete platform for Indian schools and coaching centers — attendance, fees,
              academics, parent communication, and AI exam papers. All in one place.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.34 }}
              className="flex flex-wrap gap-3 mb-8">
              <button onClick={() => navigate("/login")}
                className="flex items-center gap-2 px-7 py-4 rounded-xl font-bold text-white font-sora text-[15px] transition-all hover:scale-[1.03]"
                style={{ background: "linear-gradient(135deg,#E8690A,#F59E0B)", boxShadow: "0 8px 32px rgba(232,105,10,0.45)" }}>
                Start for Free <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => navigate("/login")}
                className="flex items-center gap-2 px-7 py-4 rounded-xl font-semibold font-sora text-[15px] transition-all hover:bg-white/10"
                style={{ color: "rgba(255,255,255,0.88)", border: "1.5px solid rgba(255,255,255,0.18)" }}>
                <Play className="w-4 h-4" /> Login
              </button>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-wrap gap-x-5 gap-y-2">
              {["No credit card required", "256-bit SSL", "SOC 2", "GDPR ready"].map(t => (
                <span key={t} className="flex items-center gap-1.5 text-[12px]" style={{ color: "rgba(255,255,255,0.38)" }}>
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />{t}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Right — Dashboard */}
          <div className="flex justify-center lg:justify-end">
            <DashboardMockup />
          </div>
        </div>
      </div>

      {/* Wave */}
      <div className="relative z-10 -mb-px">
        <svg viewBox="0 0 1440 72" fill="none" className="w-full block">
          <path d="M0 72L1440 72L1440 18C1200 56 960 0 720 26C480 52 240 6 0 36L0 72Z" fill="white" />
        </svg>
      </div>
    </section>
  );
}

/* ─── Stats Bar ───────────────────────────────────────────────── */
function StatsBar() {
  const [stats, setStats] = useState(STATS_FALLBACK);

  useEffect(() => {
    Promise.allSettled([
      schoolsApi.listPublic(),
      schoolsApi.getPublicStats(),
    ]).then(([schoolsResult, statsResult]) => {
      const schools = schoolsResult.status === "fulfilled"
        ? (Array.isArray(schoolsResult.value) ? schoolsResult.value : (schoolsResult.value?.schools || []))
        : [];
      const publicStats = statsResult.status === "fulfilled" ? statsResult.value : null;

      const schoolCount = publicStats?.schools ?? publicStats?.totalSchools ?? schools.length;
      const studentCount = publicStats?.students ?? publicStats?.totalStudents
        ?? schools.reduce((sum, s) => sum + (s.studentCount || s.students || 0), 0);
      const teacherCount = publicStats?.teachers ?? publicStats?.totalTeachers
        ?? schools.reduce((sum, s) => sum + (s.teacherCount || s.teachers || 0), 0);

      const fmt = (n) => n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K+` : `${n}+`;

      setStats([
        { value: schoolCount > 0 ? `${schoolCount}+` : "500+", label: "Schools & Coaching Centers" },
        { value: studentCount > 0 ? fmt(studentCount) : "50K+", label: "Active Students" },
        { value: teacherCount > 0 ? fmt(teacherCount) : "2,000+", label: "Teachers Empowered" },
        { value: "98%", label: "Satisfaction Rate" },
      ]);
    });
  }, []);

  const statMeta = [
    { icon: Building2, color: "#E8690A", num: null },
    { icon: Users, color: "#4F46E5", num: null },
    { icon: GraduationCap, color: "#7C3AED", num: null },
    { icon: Star, color: "#0891B2", num: null },
  ];

  return (
    <section className="bg-white py-14 sm:py-20 border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeIn>
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-100">
            {stats.map((s, i) => {
              const Icon = statMeta[i].icon;
              const color = statMeta[i].color;
              return (
                <div key={i} className="text-center px-4 sm:px-8 py-2 first:pl-0 last:pr-0">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-2xl mb-4" style={{ background: `${color}12` }}>
                    <Icon className="w-5 h-5" style={{ color }} />
                  </div>
                  <p className="font-sora font-extrabold leading-none mb-2" style={{ fontSize: "clamp(2rem,4vw,3rem)", color: "#0D1B36" }}>
                    {s.value}
                  </p>
                  <p className="text-sm text-slate-500 font-medium">{s.label}</p>
                </div>
              );
            })}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ─── Features Section ────────────────────────────────────────── */
function FeaturesSection({ activeRole, setActiveRole }) {
  const role = ROLE_TABS.find((r) => r.key === activeRole);
  const features = ROLE_FEATURES[activeRole];

  return (
    <section id="features" className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <FadeIn className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold font-sora uppercase tracking-widest mb-5"
            style={{ background: "rgba(232,105,10,0.08)", color: "#E8690A", border: "1px solid rgba(232,105,10,0.2)" }}>
            <Sparkles className="w-3 h-3" /> Everything in One Platform
          </span>
          <h2 className="font-sora font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 mb-4" style={{ letterSpacing: "-0.02em" }}>
            A portal for every role
          </h2>
          <p className="text-slate-500 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            EduPulseAI gives each stakeholder exactly what they need — no clutter, no confusion, just clarity.
          </p>
        </FadeIn>

        {/* Role tabs */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10">
          {ROLE_TABS.map((tab) => {
            const Icon = tab.icon;
            const active = tab.key === activeRole;
            return (
              <button key={tab.key} onClick={() => setActiveRole(tab.key)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 font-sora"
                style={{
                  background: active ? tab.accent : "#F8F9FC",
                  color: active ? "#fff" : "#374151",
                  border: active ? `1.5px solid ${tab.accent}` : "1.5px solid #E5E7EB",
                  boxShadow: active ? `0 4px 20px ${tab.accent}40` : "none",
                }}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>
            );
          })}
        </div>

        {/* Feature grid */}
        <AnimatePresence mode="wait">
          <motion.div key={activeRole} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }} transition={{ duration: 0.28 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feat, i) => {
              const Icon = feat.icon;
              return (
                <div key={i} className="group relative bg-white rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 cursor-default"
                  style={{ border: "1px solid #E5E7EB", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = role.accent; e.currentTarget.style.boxShadow = `0 8px 32px ${role.accent}18`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)"; }}>
                  {/* Accent top line */}
                  <div className="absolute top-0 left-6 right-6 h-0.5 rounded-b-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: role.accent }} />
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-transform duration-200 group-hover:scale-110"
                    style={{ background: `${role.accent}12`, border: `1px solid ${role.accent}25` }}>
                    <Icon className="w-5 h-5" style={{ color: role.accent }} />
                  </div>
                  <h3 className="font-sora font-bold text-slate-800 mb-2">{feat.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{feat.desc}</p>
                </div>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

/* ─── AI Section ──────────────────────────────────────────────── */
function AISection() {
  return (
    <section id="ai" className="py-20 sm:py-28 relative overflow-hidden"
      style={{ background: "linear-gradient(155deg,#060C1A 0%,#0A1428 50%,#0D1B36 100%)" }}>
      {/* Glows */}
      <div className="absolute pointer-events-none" style={{ top: "20%", left: "5%", width: 480, height: 480, borderRadius: "50%", background: "radial-gradient(circle,rgba(232,105,10,0.12) 0%,transparent 65%)" }} />
      <div className="absolute pointer-events-none" style={{ bottom: "10%", right: "5%", width: 380, height: 380, borderRadius: "50%", background: "radial-gradient(circle,rgba(124,58,237,0.1) 0%,transparent 65%)" }} />
      {/* Grid */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)", backgroundSize: "60px 60px" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        <FadeIn className="text-center mb-14">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold font-sora uppercase tracking-widest mb-5"
            style={{ background: "rgba(232,105,10,0.12)", color: "#F59E0B", border: "1px solid rgba(232,105,10,0.3)" }}>
            <Sparkles className="w-3 h-3" /> Powered by Artificial Intelligence
          </span>
          <h2 className="font-sora font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white mb-4" style={{ letterSpacing: "-0.02em" }}>
            AI that actually saves time
          </h2>
          <p className="text-base sm:text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>
            Not just a feature checkbox. Our AI tools eliminate hours of manual work for teachers and create personalized experiences for every student.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {AI_FEATURES.map((feat, i) => {
            const Icon = feat.icon;
            return (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="relative rounded-2xl p-8 overflow-hidden group transition-all duration-300 hover:-translate-y-2 h-full"
                  style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${feat.accent}28`, backdropFilter: "blur(12px)" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = `${feat.accent}55`; e.currentTarget.style.boxShadow = `0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px ${feat.accent}30`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = `${feat.accent}28`; e.currentTarget.style.boxShadow = "none"; }}>
                  {/* Top shimmer */}
                  <div className="absolute top-0 left-8 right-8 h-px" style={{ background: `linear-gradient(90deg,transparent,${feat.accent}80,transparent)` }} />
                  {/* Hover bg glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none"
                    style={{ background: `radial-gradient(ellipse at 30% 20%,${feat.accent}0d 0%,transparent 60%)` }} />

                  <div className="relative w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                    style={{ background: `${feat.accent}18`, border: `1px solid ${feat.accent}35` }}>
                    <Icon className="w-7 h-7" style={{ color: feat.accent }} />
                  </div>
                  <span className="text-[10px] font-bold font-sora uppercase tracking-[0.18em] px-2.5 py-1 rounded-full mb-4 inline-block"
                    style={{ background: `${feat.accent}18`, color: feat.accent, border: `1px solid ${feat.accent}30` }}>
                    {feat.tag}
                  </span>
                  <h3 className="font-sora font-bold text-lg text-white mb-3">{feat.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.45)" }}>{feat.desc}</p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─── How It Works ────────────────────────────────────────────── */
function HowItWorks() {
  return (
    <section className="py-20 sm:py-28" style={{ background: "#F8FAFF" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeIn className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold font-sora uppercase tracking-widest mb-5"
            style={{ background: "rgba(79,70,229,0.08)", color: "#4F46E5", border: "1px solid rgba(79,70,229,0.2)" }}>
            Simple Onboarding
          </span>
          <h2 className="font-sora font-extrabold text-3xl sm:text-4xl lg:text-5xl text-slate-900 mb-4" style={{ letterSpacing: "-0.02em" }}>
            Up and running in one day
          </h2>
          <p className="text-slate-500 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            No lengthy IT setup. No training weeks. Just sign up, add your school, and you're live.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          {/* Connector */}
          <div className="hidden md:block absolute top-12 left-1/3 right-1/3 h-px" style={{ background: "linear-gradient(90deg,transparent,#E8690A50,transparent)" }} />

          {HOW_IT_WORKS.map((step, i) => {
            const Icon = step.icon;
            const colors = ["#E8690A", "#4F46E5", "#7C3AED"];
            const c = colors[i];
            return (
              <FadeIn key={i} delay={i * 0.12}>
                <div className="bg-white rounded-2xl p-8 text-center transition-all duration-300 hover:-translate-y-1"
                  style={{ border: "1px solid #E5E7EB", boxShadow: "0 2px 16px rgba(0,0,0,0.05)" }}>
                  <div className="relative inline-flex mb-6">
                    <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: `linear-gradient(135deg,${c},${c}bb)`, boxShadow: `0 8px 28px ${c}40` }}>
                      <Icon className="w-9 h-9 text-white" />
                    </div>
                    <span className="absolute -top-3 -right-3 w-8 h-8 rounded-full flex items-center justify-center font-sora font-black text-[13px] text-white bg-slate-900 ring-4 ring-white" style={{ lineHeight: 1 }}>
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="font-sora font-bold text-xl text-slate-800 mb-3">{step.title}</h3>
                  <p className="text-slate-500 leading-relaxed text-[15px]">{step.desc}</p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─── Pricing Section ─────────────────────────────────────────── */
function PricingSection({ billingAnnual, setBillingAnnual, navigate }) {
  const [showTable, setShowTable] = useState(false);

  return (
    <section id="pricing" className="py-20 sm:py-28" style={{ background: "#F4F6FB" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        {/* ── Header ── */}
        <FadeIn className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold font-sora uppercase tracking-widest mb-5"
            style={{ background: "rgba(79,70,229,0.08)", color: "#4F46E5", border: "1px solid rgba(79,70,229,0.2)" }}>
            Transparent Pricing
          </span>
          <h2 className="font-sora font-extrabold text-3xl sm:text-4xl lg:text-5xl text-slate-900 mb-4" style={{ letterSpacing: "-0.02em" }}>
            Priced for every school in India
          </h2>
          <p className="text-slate-500 text-base sm:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            From a 50-student coaching center to a 2,000-student institution — there is a plan built for your scale. No hidden charges. No setup fees. No lock-in.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-1 bg-white border border-navy-100 rounded-2xl p-1.5 shadow-card">
            <button
              onClick={() => setBillingAnnual(false)}
              className="px-5 py-2 rounded-xl text-sm font-semibold font-sora transition-all"
              style={{ background: !billingAnnual ? "#0D1B36" : "transparent", color: !billingAnnual ? "#fff" : "#5A6278" }}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingAnnual(true)}
              className="px-5 py-2 rounded-xl text-sm font-semibold font-sora transition-all flex items-center gap-2"
              style={{ background: billingAnnual ? "#0D1B36" : "transparent", color: billingAnnual ? "#fff" : "#5A6278" }}
            >
              Annual
              <span className="text-[10px] px-2 py-0.5 rounded-md font-bold" style={{ background: "#E8690A", color: "#fff" }}>
                2 MONTHS FREE
              </span>
            </button>
          </div>
        </FadeIn>

        {/* ── Plan Cards — 5 columns ── */}
        <FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
          {PLANS.map((plan, i) => {
            const isEnterprise = plan.annualTotal === null;
            const displayPrice = billingAnnual && !isEnterprise ? plan.annualEffective : plan.monthlyPrice;
            const annualLabel = billingAnnual && !isEnterprise
              ? `₹${plan.annualTotal.toLocaleString()}/year · save ₹${plan.annualSaving.toLocaleString()}`
              : null;

            return (
              <div
                key={i}
                className="relative rounded-2xl border overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: plan.highlighted ? "linear-gradient(160deg,#0D1B36,#1E3A5F)"
                    : plan.badge === "Best Value" ? "linear-gradient(160deg,#1a0a3d,#2d1060)" : "#fff",
                  borderColor: plan.highlighted ? "#E8690A" : plan.badge === "Best Value" ? "#7C3AED" : "rgba(30,58,95,0.12)",
                  boxShadow: plan.highlighted
                    ? "0 8px 40px rgba(232,105,10,0.22), 0 0 0 1px rgba(232,105,10,0.3)"
                    : plan.badge === "Best Value" ? "0 8px 40px rgba(124,58,237,0.18), 0 0 0 1px rgba(124,58,237,0.3)"
                    : "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                {/* Badge ribbon */}
                {plan.badge && (
                  <div className="py-1.5 text-center text-[11px] font-bold font-sora text-white tracking-wide"
                    style={{ background: plan.highlighted ? "linear-gradient(90deg,#E8690A,#F59E0B)" : "linear-gradient(90deg,#7C3AED,#4F46E5)" }}>
                    ★ {plan.badge}
                  </div>
                )}

                <div className="p-5 flex flex-col flex-1">
                  {/* Plan name */}
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: plan.color }} />
                    <h3 className={`font-sora font-extrabold text-base ${plan.highlighted || plan.badge === "Best Value" ? "text-white" : "text-navy-800"}`}>
                      {plan.name}
                    </h3>
                  </div>
                  <p className={`text-[11px] mb-4 leading-snug ${plan.highlighted || plan.badge === "Best Value" ? "text-white/50" : "text-navy-400"}`}>
                    {plan.desc}
                  </p>

                  {/* Price */}
                  <div className="mb-1">
                    {plan.monthlyPrice === 0 ? (
                      <p className={`font-sora font-extrabold text-4xl ${plan.highlighted ? "text-white" : "text-navy-800"}`}>₹0</p>
                    ) : isEnterprise ? (
                      <p className={`font-sora font-extrabold text-2xl ${plan.badge === "Best Value" ? "text-white" : "text-navy-800"}`}>Custom</p>
                    ) : (
                      <div className="flex items-end gap-1">
                        <span className={`font-sora font-extrabold text-4xl leading-none ${plan.highlighted || plan.badge === "Best Value" ? "text-white" : "text-navy-800"}`}>
                          ₹{displayPrice.toLocaleString()}
                        </span>
                        <div className="mb-0.5">
                          <p className={`text-[10px] leading-tight ${plan.highlighted || plan.badge === "Best Value" ? "text-white/50" : "text-navy-400"}`}>/mo</p>
                          <p className={`text-[9px] leading-tight ${plan.highlighted || plan.badge === "Best Value" ? "text-white/30" : "text-navy-300"}`}>+GST</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Annual total / savings */}
                  {annualLabel ? (
                    <p className="text-[10px] font-semibold mb-4" style={{ color: "#22c55e" }}>{annualLabel}</p>
                  ) : isEnterprise && billingAnnual ? (
                    <p className={`text-[10px] mb-4 ${plan.badge === "Best Value" ? "text-white/40" : "text-navy-300"}`}>Negotiated annually</p>
                  ) : (
                    <div className="mb-4" />
                  )}

                  {/* Limit pills */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {[plan.limits.students + " students", plan.limits.staff + " staff", plan.limits.storage].map((pill) => (
                      <span key={pill} className="text-[9px] font-semibold px-2 py-0.5 rounded-md font-sora"
                        style={{
                          background: plan.highlighted || plan.badge === "Best Value" ? "rgba(255,255,255,0.1)" : `${plan.color}10`,
                          color: plan.highlighted || plan.badge === "Best Value" ? "rgba(255,255,255,0.65)" : plan.color,
                          border: `1px solid ${plan.highlighted || plan.badge === "Best Value" ? "rgba(255,255,255,0.12)" : plan.color + "25"}`,
                        }}>
                        {pill}
                      </span>
                    ))}
                  </div>

                  {/* Divider */}
                  <div className="mb-3" style={{ height: 1, background: plan.highlighted || plan.badge === "Best Value" ? "rgba(255,255,255,0.08)" : "rgba(30,58,95,0.08)" }} />

                  {/* Features */}
                  <ul className="space-y-1.5 mb-4 flex-1">
                    {plan.highlights.map((feat, j) => (
                      <li key={j} className="flex items-start gap-1.5">
                        <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: plan.highlighted ? "#E8690A" : plan.badge === "Best Value" ? "#A78BFA" : plan.color }} />
                        <span className={`text-[11px] leading-relaxed ${plan.highlighted || plan.badge === "Best Value" ? "text-white/65" : "text-navy-600"}`}>{feat}</span>
                      </li>
                    ))}
                    {plan.notIncluded.slice(0, 2).map((feat, j) => (
                      <li key={"no" + j} className="flex items-start gap-1.5 opacity-35">
                        <X className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: plan.highlighted || plan.badge === "Best Value" ? "#fff" : "#9AA1B4" }} />
                        <span className={`text-[11px] line-through ${plan.highlighted || plan.badge === "Best Value" ? "text-white/40" : "text-navy-400"}`}>{feat}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Support */}
                  <div className="flex items-center gap-1.5 mb-4 px-2.5 py-1.5 rounded-lg"
                    style={{ background: plan.highlighted || plan.badge === "Best Value" ? "rgba(255,255,255,0.06)" : "#F4F6FB" }}>
                    <Clock className="w-3 h-3 flex-shrink-0" style={{ color: plan.highlighted || plan.badge === "Best Value" ? "rgba(255,255,255,0.4)" : "#9AA1B4" }} />
                    <span className={`text-[10px] font-medium ${plan.highlighted || plan.badge === "Best Value" ? "text-white/45" : "text-navy-400"}`}>
                      {plan.limits.support}
                    </span>
                  </div>

                  {/* CTA */}
                  <button
                    onClick={() => navigate("/login")}
                    className="w-full py-2.5 rounded-xl font-semibold font-sora text-xs transition-all duration-200 hover:opacity-90 hover:scale-[1.01]"
                    style={
                      plan.highlighted
                        ? { background: "linear-gradient(135deg,#E8690A,#F59E0B)", color: "#fff", boxShadow: "0 4px 16px rgba(232,105,10,0.35)" }
                        : plan.badge === "Best Value"
                        ? { background: "linear-gradient(135deg,#7C3AED,#4F46E5)", color: "#fff", boxShadow: "0 4px 16px rgba(124,58,237,0.3)" }
                        : { background: `${plan.color}12`, color: plan.color, border: `1.5px solid ${plan.color}30` }
                    }
                  >
                    {plan.cta}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        </FadeIn>

        {/* ── Trust strip ── */}
        <div className="flex flex-wrap justify-center items-center gap-x-5 gap-y-2 mb-14">
          {["No credit card for trial", "Cancel anytime", "256-bit SSL encrypted", "SOC 2 compliant", "GST invoice on every payment"].map((t) => (
            <span key={t} className="flex items-center gap-1.5 text-xs text-navy-400">
              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
              {t}
            </span>
          ))}
        </div>

        {/* ── Add-ons ── */}
        <FadeIn className="mb-14">
          <div className="text-center mb-8">
            <h3 className="font-sora font-extrabold text-2xl sm:text-3xl text-navy-800 mb-2">Optional add-ons</h3>
            <p className="text-navy-500 text-sm">Bolt on exactly what you need. No bundles you don't want.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ADDONS.map((addon, i) => {
              const Icon = addon.icon;
              return (
                <div key={i}
                  className="bg-white rounded-2xl border border-navy-100 p-5 flex flex-col gap-3 hover:-translate-y-0.5 transition-all hover:shadow-card-hover">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(232,105,10,0.08)" }}>
                      <Icon className="w-5 h-5 text-brand-400" />
                    </div>
                    <span className="font-sora font-bold text-sm text-navy-800">{addon.price}</span>
                  </div>
                  <div>
                    <p className="font-sora font-semibold text-navy-800 text-sm mb-1">{addon.title}</p>
                    <p className="text-xs text-navy-500 leading-relaxed">{addon.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </FadeIn>

        {/* ── Compare all features toggle ── */}
        <div className="text-center mb-6">
          <button
            onClick={() => setShowTable(!showTable)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold font-sora border bg-white transition-all hover:shadow-card"
            style={{ color: "#1E3A5F", borderColor: "rgba(30,58,95,0.15)" }}
          >
            <BarChart2 className="w-4 h-4" />
            {showTable ? "Hide" : "Compare"} all features across plans
            <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showTable ? "rotate-180" : ""}`} />
          </button>
        </div>

        {/* ── Comparison Table ── */}
        <AnimatePresence>
          {showTable && (
            <motion.div
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.4, ease: "easeInOut" }}
              className="overflow-hidden mb-14"
            >
              <div className="bg-white rounded-2xl border border-navy-100 overflow-x-auto shadow-card">
                <table className="w-full text-sm min-w-[700px]">
                  <thead>
                    <tr style={{ background: "#0D1B36" }}>
                      <th className="text-left px-5 py-4 font-sora font-semibold text-white/50 text-xs w-52">Feature</th>
                      {PLANS.map((plan) => (
                        <th key={plan.name} className="px-3 py-4 text-center font-sora font-bold text-sm"
                          style={{ color: plan.highlighted ? "#F59E0B" : plan.badge === "Best Value" ? "#A78BFA" : "#fff" }}>
                          {plan.name}
                          {plan.badge && <span className="block text-[9px] font-normal opacity-50 mt-0.5">{plan.badge}</span>}
                        </th>
                      ))}
                    </tr>
                    {/* Monthly price row */}
                    <tr style={{ background: "#111D35" }}>
                      <td className="px-5 py-2 text-xs text-white/30 font-medium">Monthly price</td>
                      {PLANS.map((plan) => (
                        <td key={plan.name} className="px-3 py-2 text-center">
                          <span className="font-sora font-bold text-xs" style={{ color: plan.highlighted ? "#F59E0B" : "#fff" }}>
                            {plan.monthlyPrice === 0 ? "Free" : plan.annualTotal === null ? "Custom" : `₹${plan.monthlyPrice.toLocaleString()}`}
                          </span>
                        </td>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARISON_ROWS.map((row, i) => {
                      if (row.category) {
                        return (
                          <tr key={i} style={{ background: "#F4F6FB" }}>
                            <td colSpan={6} className="px-5 py-2.5 font-sora font-semibold text-xs text-navy-500 uppercase tracking-widest">
                              {row.category}
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={i} className="border-t border-navy-50 hover:bg-navy-50/40 transition-colors">
                          <td className="px-5 py-3 text-navy-600 text-xs">{row.label}</td>
                          {row.values.map((val, j) => {
                            const plan = PLANS[j];
                            const dark = plan.highlighted || plan.badge === "Best Value";
                            let display;
                            if (val === true) display = <CheckCircle className="w-4 h-4 mx-auto" style={{ color: plan.highlighted ? "#E8690A" : plan.badge === "Best Value" ? "#7C3AED" : "#1A7A4A" }} />;
                            else if (val === false) display = <X className="w-4 h-4 mx-auto text-navy-200" />;
                            else display = <span className={`font-semibold text-[11px] ${dark ? "text-white/70" : "text-navy-700"}`}>{val}</span>;
                            return (
                              <td key={j} className="px-3 py-3 text-center"
                                style={{ background: plan.highlighted ? "rgba(232,105,10,0.03)" : plan.badge === "Best Value" ? "rgba(124,58,237,0.03)" : "transparent" }}>
                                {display}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </section>
  );
}

/* ─── Testimonials ────────────────────────────────────────────── */
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#E8690A,#F59E0B)",
  "linear-gradient(135deg,#4F46E5,#7C3AED)",
  "linear-gradient(135deg,#0891B2,#4F46E5)",
  "linear-gradient(135deg,#1A7A4A,#0891B2)",
  "linear-gradient(135deg,#7C3AED,#E8690A)",
  "linear-gradient(135deg,#C0392B,#7C3AED)",
];

function TestimonialsSection() {
  const [reviews, setReviews] = useState(TESTIMONIALS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reviewsApi.listPublic()
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.reviews || []);
        if (list.length > 0) {
          const sorted = [...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setReviews(sorted.map((r) => ({
            quote: r.quote || r.review || r.text,
            name: r.name || r.author,
            role: r.role || r.designation,
            avatar: (r.name || r.author || "U").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase(),
            rating: r.rating || 5,
          })));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const displayReviews = reviews.slice(0, 6);

  return (
    <section id="testimonials" className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <FadeIn className="text-center mb-14">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold font-sora uppercase tracking-widest mb-5"
            style={{ background: "rgba(232,105,10,0.08)", color: "#E8690A", border: "1px solid rgba(232,105,10,0.2)" }}>
            <Star className="w-3 h-3 fill-current" /> Loved by Educators
          </span>
          <h2 className="font-sora font-extrabold text-3xl sm:text-4xl lg:text-5xl text-slate-900 mb-4" style={{ letterSpacing: "-0.02em" }}>
            Trusted by schools across India
          </h2>
        </FadeIn>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <div key={i} className="rounded-2xl h-52 animate-pulse" style={{ background: "#F1F5F9" }} />)}
          </div>
        ) : (
          <div className={`grid grid-cols-1 md:grid-cols-${Math.min(displayReviews.length, 3)} gap-6`}>
            {displayReviews.map((t, i) => {
              const dark = i % 3 === 1;
              return (
                <FadeIn key={i} delay={i * 0.1}>
                  <div className="relative rounded-2xl p-7 flex flex-col h-full transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                    style={dark
                      ? { background: "linear-gradient(155deg,#0D1B36,#1a2f58)", border: "1px solid rgba(232,105,10,0.2)", boxShadow: "0 12px 48px rgba(0,0,0,0.18)" }
                      : { background: "#fff", border: "1px solid #E5E7EB", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                    {/* Decorative quote */}
                    <div className="absolute top-4 right-5 font-black leading-none select-none pointer-events-none"
                      style={{ fontSize: 72, color: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)", fontFamily: "Georgia,serif" }}>"</div>

                    <div className="flex gap-0.5 mb-5">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <Star key={j} className="w-4 h-4 fill-current text-orange-400" />
                      ))}
                    </div>
                    <p className="leading-relaxed text-[15px] flex-1 mb-6" style={{ color: dark ? "rgba(255,255,255,0.72)" : "#374151" }}>
                      "{t.quote}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center font-sora font-bold text-sm text-white shrink-0"
                        style={{ background: AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length] }}>
                        {t.avatar}
                      </div>
                      <div>
                        <p className="font-sora font-semibold text-sm" style={{ color: dark ? "#fff" : "#0F172A" }}>{t.name}</p>
                        <p className="text-xs mt-0.5" style={{ color: dark ? "rgba(255,255,255,0.42)" : "#94A3B8" }}>{t.role}</p>
                      </div>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── CTA Section ─────────────────────────────────────────────── */
function CTASection({ navigate }) {
  return (
    <section className="py-24 sm:py-32 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg,#06091A 0%,#0A1428 50%,#0D1B36 100%)" }}>
      {/* Grid */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.03) 1px,transparent 1px)", backgroundSize: "60px 60px" }} />
      {/* Glow */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div style={{ width: 600, height: 300, borderRadius: "50%", background: "radial-gradient(ellipse,rgba(232,105,10,0.2) 0%,transparent 70%)" }} />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <FadeIn>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold font-sora uppercase tracking-widest mb-7"
            style={{ background: "rgba(232,105,10,0.12)", color: "#F59E0B", border: "1px solid rgba(232,105,10,0.3)" }}>
            <Award className="w-3.5 h-3.5" /> Join 500+ Schools Today
          </span>

          <h2 className="font-sora font-extrabold text-white mb-5" style={{ fontSize: "clamp(2rem,4.5vw,3.5rem)", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
            Ready to transform<br />your school?
          </h2>

          <p className="text-[17px] leading-relaxed mb-10 max-w-xl mx-auto" style={{ color: "rgba(255,255,255,0.52)" }}>
            Start free today. No credit card required. Set up your school in under 10 minutes and see the difference EduPulseAI makes.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => navigate("/login")}
              className="flex items-center gap-2 px-9 py-4 rounded-xl font-bold text-white font-sora text-base transition-all hover:scale-[1.04]"
              style={{ background: "linear-gradient(135deg,#E8690A,#F59E0B)", boxShadow: "0 10px 40px rgba(232,105,10,0.5)" }}>
              Get Started Free <ArrowRight className="w-5 h-5" />
            </button>
            <button onClick={() => navigate("/login")}
              className="flex items-center gap-2 px-9 py-4 rounded-xl font-semibold font-sora text-base transition-all hover:bg-white/8"
              style={{ color: "rgba(255,255,255,0.8)", border: "1.5px solid rgba(255,255,255,0.18)" }}>
              Login to Dashboard
            </button>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ─── FAQ Modal Content (extracted so hooks are valid) ────────── */
function FAQModalContent() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div>
      <h2 className="font-sora font-extrabold text-2xl text-navy-800 mb-1">Frequently Asked Questions</h2>
      <p className="text-navy-500 text-sm mb-6">Everything you need to know about pricing, billing, and getting started.</p>
      <div className="space-y-3">
        {PRICING_FAQ.map((item, i) => (
          <div key={i} className="rounded-2xl border border-navy-100 overflow-hidden">
            <button
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
              className="w-full flex items-center justify-between px-5 py-4 text-left font-sora font-semibold text-navy-800 text-sm hover:bg-navy-50 transition-colors"
            >
              {item.q}
              <ChevronDown
                className="w-4 h-4 text-navy-400 flex-shrink-0 ml-2 transition-transform duration-200"
                style={{ transform: openIdx === i ? "rotate(180deg)" : "none" }}
              />
            </button>
            {openIdx === i && (
              <div className="px-5 pb-5 text-sm text-navy-600 leading-relaxed border-t border-navy-50">
                <div className="pt-4">{item.a}</div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-2xl p-5 border text-center" style={{ background: "rgba(79,70,229,0.04)", borderColor: "rgba(79,70,229,0.15)" }}>
        <p className="text-navy-600 text-sm mb-1 font-medium">Still have questions?</p>
        <p className="text-navy-400 text-xs mb-3">Our team responds within 24 hours.</p>
        <a href="mailto:edupulseai.official@gmail.com" className="text-accent-purple font-semibold font-sora text-sm hover:underline">
          edupulseai.official@gmail.com
        </a>
      </div>
    </div>
  );
}

/* ─── Footer Modal Content ────────────────────────────────────── */
const MODAL_CONTENT = {
  "About Us": {
    icon: Heart,
    accent: "#E8690A",
    render: () => (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl overflow-hidden shrink-0" style={{ boxShadow: "0 8px 24px rgba(79,70,229,0.3)" }}>
            <img src="/logo192.png" alt="EduPulseAI" className="w-full h-full object-cover" />
          </div>
          <div>
            <h2 className="font-sora font-extrabold text-2xl text-navy-800">About EduPulseAI</h2>
            <p className="text-sm text-navy-400">Building the future of Indian education</p>
          </div>
        </div>

        <div className="rounded-2xl p-5 mb-6 border border-navy-100" style={{ background: "#F4F6FB" }}>
          <p className="text-navy-700 leading-relaxed">
            EduPulseAI was founded in 2026 with a single mission: to make world-class school management tools accessible to every school and coaching center in India — from metro cities to small towns. We believe technology should simplify education, not complicate it.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { value: "2026", label: "Founded" },
            { value: "500+", label: "Schools" },
            { value: "50K+", label: "Students" },
          ].map((s) => (
            <div key={s.label} className="text-center rounded-2xl py-4 px-3 border border-navy-100 bg-white">
              <p className="font-sora font-extrabold text-2xl text-gradient-brand mb-1">{s.value}</p>
              <p className="text-xs text-navy-400">{s.label}</p>
            </div>
          ))}
        </div>

        <h3 className="font-sora font-bold text-navy-800 mb-4">Our Core Values</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          {[
            { icon: Star, title: "Student-First", desc: "Every feature we build asks: does this help students learn better?" },
            { icon: Shield, title: "Trustworthy", desc: "We handle school data with the same care we'd give our own children's data." },
            { icon: Sparkles, title: "AI-Driven", desc: "We use AI where it genuinely saves time — not as a buzzword." },
            { icon: Globe, title: "India-First", desc: "Built for CBSE, ICSE, and state boards. Priced for every school budget." },
          ].map((v) => {
            const Icon = v.icon;
            return (
              <div key={v.title} className="flex gap-3 p-4 rounded-xl border border-navy-100 bg-white">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(232,105,10,0.1)" }}>
                  <Icon className="w-4 h-4 text-brand-400" />
                </div>
                <div>
                  <p className="font-sora font-semibold text-navy-800 text-sm">{v.title}</p>
                  <p className="text-xs text-navy-500 mt-0.5">{v.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl p-5 border" style={{ background: "linear-gradient(135deg,#0D1B36,#1E3A5F)", borderColor: "rgba(232,105,10,0.2)" }}>
          <p className="text-white/80 text-sm leading-relaxed">
            🏢 <strong className="text-white">Headquarters:</strong> Noida, Uttar Pradesh, India<br />
            👥 <strong className="text-white">Team:</strong> 25+ engineers, educators & designers<br />
            📞 <strong className="text-white">Phone:</strong> +91 98737 62936<br />
            📧 <strong className="text-white">General Inquiries:</strong> edupulseai.official@gmail.com
          </p>
        </div>
      </div>
    ),
  },

  "Blog": {
    icon: BookOpen,
    accent: "#4F46E5",
    render: () => (
      <div>
        <h2 className="font-sora font-extrabold text-2xl text-navy-800 mb-2">EduPulseAI Blog</h2>
        <p className="text-navy-500 mb-8">Insights, guides, and stories from the world of school management and EdTech.</p>

        <div className="space-y-5">
          {[
            {
              category: "AI in Education",
              title: "How AI is Transforming School Management in India",
              excerpt: "From automated attendance to AI-generated exam papers, we explore the five biggest ways artificial intelligence is helping school principals reclaim their time.",
              date: "May 20, 2025",
              readTime: "6 min read",
              accent: "#E8690A",
            },
            {
              category: "Teacher Productivity",
              title: "5 Ways EduPulseAI Reduces Teacher Workload by 40%",
              excerpt: "Teachers spend 30% of their time on administrative tasks. Here's exactly how the right digital tools can give that time back — with real numbers from our schools.",
              date: "April 12, 2025",
              readTime: "5 min read",
              accent: "#4F46E5",
            },
            {
              category: "Fee Management",
              title: "The Complete Guide to Digital Fee Management for Schools",
              excerpt: "Manual fee registers and WhatsApp reminders are costing schools thousands. This guide walks through setting up an automated fee collection system that just works.",
              date: "March 28, 2025",
              readTime: "8 min read",
              accent: "#0891B2",
            },
            {
              category: "Student Success",
              title: "Gamification in Education: Why Students Love EduPulseAI's Tier System",
              excerpt: "Bronze, Silver, Gold, Platinum, Diamond, Crown — the six tiers that turned homework completion from a chore into a challenge students actually care about.",
              date: "March 5, 2025",
              readTime: "4 min read",
              accent: "#7C3AED",
            },
          ].map((post) => (
            <div key={post.title} className="rounded-2xl border border-navy-100 p-5 bg-white hover:shadow-card-hover transition-all duration-200 group cursor-pointer">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold font-sora px-2.5 py-1 rounded-full" style={{ background: `${post.accent}12`, color: post.accent }}>
                  {post.category}
                </span>
                <div className="flex items-center gap-1.5 text-xs text-navy-400">
                  <Clock className="w-3 h-3" />
                  {post.readTime}
                </div>
              </div>
              <h3 className="font-sora font-bold text-navy-800 mb-2 group-hover:text-brand-400 transition-colors">{post.title}</h3>
              <p className="text-sm text-navy-500 leading-relaxed mb-3">{post.excerpt}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-navy-400">{post.date}</span>
                <span className="flex items-center gap-1 text-xs font-semibold text-brand-400">
                  Read more <ExternalLink className="w-3 h-3" />
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-2xl p-5 border text-center" style={{ background: "#F4F6FB", borderColor: "rgba(30,58,95,0.1)" }}>
          <p className="text-navy-600 text-sm mb-3">Get new articles in your inbox every week.</p>
          <div className="flex gap-2 max-w-sm mx-auto">
            <input
              type="email"
              placeholder="your@school.com"
              className="flex-1 px-4 py-2.5 rounded-xl border border-navy-200 text-sm outline-none focus:border-brand-400 bg-white"
            />
            <button className="px-4 py-2.5 rounded-xl gradient-brand text-white text-sm font-semibold font-sora">
              Subscribe
            </button>
          </div>
        </div>
      </div>
    ),
  },

  "Careers": {
    icon: Briefcase,
    accent: "#7C3AED",
    render: () => (
      <div>
        <h2 className="font-sora font-extrabold text-2xl text-navy-800 mb-2">Join Our Team</h2>
        <p className="text-navy-500 mb-6">Help us build the platform that empowers a million students across India.</p>

        <div className="rounded-2xl p-5 mb-8 border" style={{ background: "linear-gradient(135deg,#0D1B36,#1E3A5F)", borderColor: "rgba(79,70,229,0.3)" }}>
          <div className="flex gap-3 mb-3">
            {["🚀 Remote-friendly", "🏥 Health benefits", "📚 Learning budget", "💰 Competitive pay"].map((b) => (
              <span key={b} className="text-xs text-white/70 bg-white/8 px-2.5 py-1 rounded-full border border-white/10">{b}</span>
            ))}
          </div>
          <p className="text-white/70 text-sm leading-relaxed">
            We're a small, high-impact team that moves fast and ships things that genuinely help educators. If you care about education and love building things, you'll fit right in.
          </p>
        </div>

        <h3 className="font-sora font-bold text-navy-800 mb-4">Open Positions</h3>
        <div className="space-y-4 mb-8">
          {[
            {
              title: "Full-Stack Engineer",
              type: "Full-time · Remote",
              dept: "Engineering",
              desc: "Build and maintain core features of our React + Node.js platform. Experience with real-time systems (Socket.io) and AI integrations a plus.",
              accent: "#4F46E5",
            },
            {
              title: "AI / ML Engineer",
              type: "Full-time · Remote",
              dept: "Engineering",
              desc: "Design and improve our AI study planner, exam paper builder, and quiz generation engine. Strong Python and LLM integration experience required.",
              accent: "#7C3AED",
            },
            {
              title: "Customer Success Manager",
              type: "Full-time · Delhi / Hybrid",
              dept: "Operations",
              desc: "Onboard new schools, run training sessions, and be the voice of our customers internally. Teaching or school admin background a huge plus.",
              accent: "#E8690A",
            },
            {
              title: "UI / UX Designer",
              type: "Contract · Remote",
              dept: "Design",
              desc: "Design intuitive experiences for teachers, students, and parents across mobile and desktop. Deep understanding of India's diverse user base preferred.",
              accent: "#0891B2",
            },
          ].map((job) => (
            <div key={job.title} className="rounded-2xl border border-navy-100 p-5 bg-white hover:shadow-card-hover transition-all duration-200">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="font-sora font-bold text-navy-800">{job.title}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: `${job.accent}12`, color: job.accent }}>{job.dept}</span>
                    <span className="text-xs text-navy-400">{job.type}</span>
                  </div>
                </div>
                <button className="text-xs font-semibold font-sora px-3 py-1.5 rounded-lg gradient-brand text-white flex-shrink-0">
                  Apply
                </button>
              </div>
              <p className="text-sm text-navy-500 leading-relaxed">{job.desc}</p>
            </div>
          ))}
        </div>

        <div className="rounded-2xl p-5 border border-navy-100 bg-white text-center">
          <p className="text-navy-600 text-sm mb-1 font-medium">Don't see your role?</p>
          <p className="text-navy-400 text-xs mb-3">We're always looking for exceptional people. Send your resume and we'll keep it on file.</p>
          <a href="mailto:careers@edupulseai.co.in" className="text-brand-400 text-sm font-semibold font-sora hover:underline">
            careers@edupulseai.co.in
          </a>
        </div>
      </div>
    ),
  },

  "Contact": {
    icon: Mail,
    accent: "#0891B2",
    render: () => {
      return <ContactForm />;
    },
  },

  "Privacy Policy": {
    icon: Shield,
    accent: "#1A7A4A",
    render: () => (
      <div>
        <h2 className="font-sora font-extrabold text-2xl text-navy-800 mb-1">Privacy Policy</h2>
        <p className="text-xs text-navy-400 mb-6">Last updated: January 15, 2025</p>

        {[
          {
            title: "1. Information We Collect",
            content: "We collect information you provide directly to us when you create an account, such as your name, email address, school name, and role. We also collect information about how you use EduPulseAI, including log data (IP addresses, browser type, pages visited), device information, and usage patterns to improve our service. Student data is only collected with the explicit consent of the school administration.",
          },
          {
            title: "2. How We Use Your Information",
            content: "We use the information we collect to provide, maintain, and improve EduPulseAI; send transactional emails (OTPs, receipts, announcements); respond to your support requests; send service updates and platform notifications; and comply with legal obligations. We do not sell, trade, or rent your personal information to third parties.",
          },
          {
            title: "3. Data Storage & Security",
            content: "All data is stored on secure servers in India with AES-256 encryption at rest and TLS 1.3 in transit. We implement industry-standard security measures including regular penetration testing, intrusion detection, automated backups every 6 hours, and role-based access controls to ensure only authorized personnel can access your data.",
          },
          {
            title: "4. Data Retention",
            content: "We retain your account data for as long as your subscription is active. Upon account termination, you may request complete data deletion within 30 days. Student academic records are retained per applicable Indian educational regulations (typically 7 years) unless the school admin requests earlier deletion.",
          },
          {
            title: "5. Cookies",
            content: "We use essential cookies to keep you logged in and remember your preferences. We do not use advertising cookies or sell cookie data. You can disable non-essential cookies in your browser settings without affecting core functionality.",
          },
          {
            title: "6. Your Rights",
            content: "You have the right to access, correct, or delete your personal data at any time by contacting us at privacy@edupulseai.co.in. School administrators may manage student data through the admin panel. For GDPR-related requests, we respond within 72 hours.",
          },
          {
            title: "7. Contact",
            content: "For privacy concerns or data requests, contact our Data Protection Officer at edupulseai.official@gmail.com or write to: EduPulseAI Privacy Team, Noida, Uttar Pradesh, India 201301.",
          },
        ].map((section) => (
          <div key={section.title} className="mb-6">
            <h3 className="font-sora font-semibold text-navy-800 mb-2">{section.title}</h3>
            <p className="text-sm text-navy-600 leading-relaxed">{section.content}</p>
          </div>
        ))}
      </div>
    ),
  },

  "Terms of Service": {
    icon: FileText,
    accent: "#E8690A",
    render: () => (
      <div>
        <h2 className="font-sora font-extrabold text-2xl text-navy-800 mb-1">Terms of Service</h2>
        <p className="text-xs text-navy-400 mb-6">Last updated: January 15, 2025 · Effective: February 1, 2025</p>

        <div className="rounded-xl p-4 mb-6 border" style={{ background: "rgba(232,105,10,0.05)", borderColor: "rgba(232,105,10,0.2)" }}>
          <p className="text-sm text-navy-700 leading-relaxed">
            By using EduPulseAI, you agree to these terms. Please read them carefully. If you are subscribing on behalf of a school or organization, you confirm that you have the authority to bind that organization.
          </p>
        </div>

        {[
          {
            title: "1. Acceptance of Terms",
            content: "By accessing or using EduPulseAI ('Service'), you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree, do not use the Service. We may update these terms from time to time, and continued use after changes constitutes acceptance.",
          },
          {
            title: "2. Use of Service",
            content: "EduPulseAI grants you a limited, non-exclusive, non-transferable license to use the Service for your school's internal educational management purposes. You may not sublicense, sell, resell, transfer, or assign the Service to any third party.",
          },
          {
            title: "3. User Accounts",
            content: "You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account. You must immediately notify us of any unauthorized use of your account. School administrators are responsible for all sub-accounts (teachers, students, parents) created under their school.",
          },
          {
            title: "4. Prohibited Activities",
            content: "You may not: (a) attempt to gain unauthorized access to any part of the Service; (b) use the Service to transmit spam, malware, or illegal content; (c) reverse engineer or copy any part of the platform; (d) use the Service in a way that violates any applicable law or harms minors; (e) upload content that infringes third-party intellectual property rights.",
          },
          {
            title: "5. Subscription & Billing",
            content: "Paid subscriptions are billed monthly or annually as selected. All prices are in Indian Rupees (INR) and inclusive of applicable GST. You may cancel at any time; cancellation takes effect at the end of the current billing period with no pro-rata refunds. We reserve the right to modify pricing with 30 days' notice.",
          },
          {
            title: "6. Limitation of Liability",
            content: "To the maximum extent permitted by law, EduPulseAI shall not be liable for any indirect, incidental, special, consequential, or punitive damages. Our total liability for any claim arising from your use of the Service shall not exceed the amount you paid us in the 12 months preceding the claim.",
          },
          {
            title: "7. Governing Law",
            content: "These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Noida, Uttar Pradesh, India. For disputes under ₹10 lakhs, we encourage resolution via our support team before pursuing legal action.",
          },
        ].map((section) => (
          <div key={section.title} className="mb-6">
            <h3 className="font-sora font-semibold text-navy-800 mb-2">{section.title}</h3>
            <p className="text-sm text-navy-600 leading-relaxed">{section.content}</p>
          </div>
        ))}
      </div>
    ),
  },

  "Security": {
    icon: Shield,
    accent: "#1A7A4A",
    render: () => (
      <div>
        <h2 className="font-sora font-extrabold text-2xl text-navy-800 mb-2">Security at EduPulseAI</h2>
        <p className="text-navy-500 mb-8">We treat your school's data like our own. Here's exactly how we protect it.</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[
            { label: "256-bit AES", sub: "Encryption at rest", icon: Lock, accent: "#1A7A4A" },
            { label: "SOC 2 Type II", sub: "Compliance certified", icon: Award, accent: "#4F46E5" },
            { label: "GDPR Ready", sub: "Data rights respected", icon: Shield, accent: "#0891B2" },
          ].map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.label} className="rounded-2xl p-5 border text-center" style={{ background: `${b.accent}08`, borderColor: `${b.accent}25` }}>
                <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: `${b.accent}15` }}>
                  <Icon className="w-6 h-6" style={{ color: b.accent }} />
                </div>
                <p className="font-sora font-bold text-navy-800 text-sm">{b.label}</p>
                <p className="text-xs text-navy-400 mt-0.5">{b.sub}</p>
              </div>
            );
          })}
        </div>

        {[
          {
            icon: Lock,
            title: "Encryption",
            items: [
              "AES-256 encryption for all data at rest",
              "TLS 1.3 for all data in transit",
              "End-to-end encrypted chat messages",
              "Encrypted database backups",
            ],
          },
          {
            icon: Shield,
            title: "Infrastructure",
            items: [
              "Hosted on ISO 27001-certified data centers in India",
              "Automated backups every 6 hours with 30-day retention",
              "99.9% uptime SLA with redundant architecture",
              "DDoS protection via Cloudflare",
            ],
          },
          {
            icon: Users,
            title: "Access Control",
            items: [
              "Role-based access control (RBAC) for every user",
              "OTP-based authentication — no passwords to steal",
              "Session expiry after inactivity",
              "Audit logs for all admin actions",
            ],
          },
          {
            icon: CheckCircle,
            title: "Compliance & Testing",
            items: [
              "Annual third-party penetration testing",
              "OWASP Top 10 vulnerability assessments",
              "GDPR-compliant data handling and deletion",
              "India IT Act & DPDP Act 2023 compliant",
            ],
          },
        ].map((section) => {
          const Icon = section.icon;
          return (
            <div key={section.title} className="rounded-2xl border border-navy-100 p-5 bg-white mb-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "rgba(26,122,74,0.1)" }}>
                  <Icon className="w-4 h-4" style={{ color: "#1A7A4A" }} />
                </div>
                <h3 className="font-sora font-semibold text-navy-800">{section.title}</h3>
              </div>
              <ul className="space-y-2">
                {section.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-navy-600">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#1A7A4A" }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        <div className="rounded-2xl p-5 border text-center mt-6" style={{ background: "#F4F6FB", borderColor: "rgba(30,58,95,0.1)" }}>
          <p className="text-navy-600 text-sm mb-1 font-medium">Found a security issue?</p>
          <p className="text-navy-400 text-xs mb-3">We take responsible disclosure seriously. Report vulnerabilities to:</p>
          <a href="mailto:security@edupulseai.co.in" className="text-brand-400 font-semibold font-sora text-sm hover:underline">
            security@edupulseai.co.in
          </a>
        </div>
      </div>
    ),
  },

  "FAQ": {
    icon: ChevronDown,
    accent: "#4F46E5",
    render: () => <FAQModalContent />,
  },
};

/* ─── Contact Form (stateful, extracted to avoid hooks-in-render) */
function ContactForm() {
  const [form, setForm] = useState({ name: "", school: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  if (sent) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-2xl gradient-brand flex items-center justify-center mx-auto mb-4 shadow-glow-brand">
          <CheckCircle className="w-8 h-8 text-white" />
        </div>
        <h3 className="font-sora font-bold text-xl text-navy-800 mb-2">Message sent!</h3>
        <p className="text-navy-500 text-sm">We'll get back to you within 24 hours at <strong>{form.email}</strong>.</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="font-sora font-extrabold text-2xl text-navy-800 mb-2">Get in Touch</h2>
      <p className="text-navy-500 mb-6">Questions, demos, or just want to chat? We respond within 24 hours.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[
          { icon: Mail, label: "Email", value: "edupulseai.official@gmail.com", accent: "#E8690A" },
          { icon: Phone, label: "Phone", value: "+91 98737 62936", accent: "#4F46E5" },
          { icon: MapPin, label: "Office", value: "Noida, Uttar Pradesh, India", accent: "#0891B2" },
        ].map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="rounded-2xl p-4 border text-center" style={{ background: `${c.accent}08`, borderColor: `${c.accent}20` }}>
              <div className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ background: `${c.accent}15` }}>
                <Icon className="w-5 h-5" style={{ color: c.accent }} />
              </div>
              <p className="text-xs text-navy-400 mb-0.5">{c.label}</p>
              <p className="text-sm font-semibold text-navy-700 font-sora">{c.value}</p>
            </div>
          );
        })}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-navy-600 mb-1.5 font-sora">Your Name *</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Rajesh Kumar"
              className="w-full px-4 py-3 rounded-xl border border-navy-200 text-sm outline-none focus:border-brand-400 bg-white text-navy-800 placeholder-navy-300 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy-600 mb-1.5 font-sora">School / Organization *</label>
            <input
              required
              value={form.school}
              onChange={(e) => setForm({ ...form, school: e.target.value })}
              placeholder="Delhi Public Academy"
              className="w-full px-4 py-3 rounded-xl border border-navy-200 text-sm outline-none focus:border-brand-400 bg-white text-navy-800 placeholder-navy-300 transition-colors"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-navy-600 mb-1.5 font-sora">Email Address *</label>
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="principal@school.in"
            className="w-full px-4 py-3 rounded-xl border border-navy-200 text-sm outline-none focus:border-brand-400 bg-white text-navy-800 placeholder-navy-300 transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-navy-600 mb-1.5 font-sora">Message *</label>
          <textarea
            required
            rows={4}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="Tell us about your school and how we can help..."
            className="w-full px-4 py-3 rounded-xl border border-navy-200 text-sm outline-none focus:border-brand-400 bg-white text-navy-800 placeholder-navy-300 transition-colors resize-none"
          />
        </div>
        <button
          type="submit"
          className="w-full py-3.5 rounded-xl font-semibold font-sora text-sm text-white gradient-brand shadow-glow-brand flex items-center justify-center gap-2 hover:opacity-90 transition-all"
        >
          Send Message
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

/* ─── Footer Modal ────────────────────────────────────────────── */
function FooterModal({ modalKey, onClose }) {
  const content = MODAL_CONTENT[modalKey];
  const Icon = content ? content.icon : null;

  // Must be before any early return (Rules of Hooks)
  useEffect(() => {
    if (!content) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, content]);

  if (!content) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[999] flex items-center justify-center p-4"
        style={{ background: "rgba(10,15,30,0.85)", backdropFilter: "blur(8px)" }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          key="panel"
          initial={{ opacity: 0, scale: 0.93, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.93, y: 24 }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="relative w-full max-w-2xl max-h-[88vh] flex flex-col rounded-3xl overflow-hidden bg-white shadow-modal"
        >
          {/* Modal header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-navy-100 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${content.accent}12` }}>
                <Icon className="w-4.5 h-4.5" style={{ color: content.accent, width: 18, height: 18 }} />
              </div>
              <span className="font-sora font-bold text-navy-800">{modalKey}</span>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-navy-50 transition-all text-navy-400 hover:text-navy-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto flex-1 px-6 py-6">
            {content.render()}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ─── Footer ──────────────────────────────────────────────────── */
function Footer({ onNav }) {
  const [activeModal, setActiveModal] = useState(null);

  const productLinks = [
    { label: "Features", href: "#features", modal: false },
    { label: "AI Tools", href: "#ai", modal: false },
    { label: "Pricing", href: "#pricing", modal: false },
    { label: "School Websites", href: "#", modal: false },
  ];
  const companyLinks = [
    { label: "About Us", modal: true },
    { label: "Blog", modal: true },
    { label: "Careers", modal: true },
    { label: "Contact", modal: true },
    { label: "FAQ", modal: true },
  ];
  const legalLinks = [
    { label: "Privacy Policy", modal: true },
    { label: "Terms of Service", modal: true },
    { label: "Security", modal: true },
  ];

  return (
    <>
      {activeModal && <FooterModal modalKey={activeModal} onClose={() => setActiveModal(null)} />}

      <footer className="pt-16 pb-8" style={{ background: "#0A0F1E" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">
            {/* Brand */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-xl overflow-hidden shrink-0" style={{ boxShadow: "0 4px 14px rgba(79,70,229,0.35)" }}>
                  <img src="/logo192.png" alt="EduPulseAI" className="w-full h-full object-cover" />
                </div>
                <span className="font-sora font-bold text-lg text-white">
                  EduPulse<span className="text-gradient-brand">AI</span>
                </span>
              </div>
              <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(255,255,255,0.4)" }}>
                The AI-powered school management platform built for India — empowering principals, teachers, students, and parents.
              </p>
              <div className="space-y-2 mb-5">
                {[
                  { Icon: Mail, text: "edupulseai.official@gmail.com" },
                  { Icon: Phone, text: "+91 98737 62936" },
                  { Icon: MapPin, text: "Noida, Uttar Pradesh, India" },
                ].map(({ Icon, text }) => (
                  <div key={text} className="flex items-center gap-2 text-xs" style={{ color: "rgba(255,255,255,0.35)" }}>
                    <Icon className="w-3 h-3 flex-shrink-0" style={{ color: "rgba(255,255,255,0.3)" }} />
                    {text}
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                {[Globe, Smartphone, MessageSquare].map((Icon, i) => (
                  <div key={i} className="w-9 h-9 rounded-xl flex items-center justify-center border border-white/10 hover:border-white/25 transition-all cursor-pointer" style={{ background: "rgba(255,255,255,0.04)" }}>
                    <Icon className="w-4 h-4" style={{ color: "rgba(255,255,255,0.4)" }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Product */}
            <div>
              <p className="text-white font-sora font-semibold text-sm mb-4">Product</p>
              <ul className="space-y-2.5">
                {productLinks.map((l) => (
                  <li key={l.label}>
                    <button
                      onClick={() => l.href?.startsWith("#") ? onNav(l.href) : null}
                      className="text-sm hover:text-white transition-colors text-left"
                      style={{ color: "rgba(255,255,255,0.4)" }}
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <p className="text-white font-sora font-semibold text-sm mb-4">Company</p>
              <ul className="space-y-2.5">
                {companyLinks.map((l) => (
                  <li key={l.label}>
                    <button
                      onClick={() => setActiveModal(l.label)}
                      className="text-sm hover:text-white transition-colors text-left group flex items-center gap-1"
                      style={{ color: "rgba(255,255,255,0.4)" }}
                    >
                      {l.label}
                      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <p className="text-white font-sora font-semibold text-sm mb-4">Legal</p>
              <ul className="space-y-2.5">
                {legalLinks.map((l) => (
                  <li key={l.label}>
                    <button
                      onClick={() => setActiveModal(l.label)}
                      className="text-sm hover:text-white transition-colors text-left group flex items-center gap-1"
                      style={{ color: "rgba(255,255,255,0.4)" }}
                    >
                      {l.label}
                      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/5">
            <p className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
              © 2026 EduPulseAI. All rights reserved. Made with ❤️ for Indian schools.
            </p>
            <div className="flex items-center gap-4">
              {["256-bit SSL", "SOC 2", "GDPR"].map((badge) => (
                <span key={badge} className="flex items-center gap-1.5 text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
                  <Shield className="w-3 h-3 text-green-400" />
                  {badge}
                </span>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

/* ─── Global float keyframes injected once ────────────────────── */
const floatStyles = `
  @keyframes float1 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
  @keyframes float2 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(6px)} }
`;

/* ─── Main Export ─────────────────────────────────────────────── */
export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeRole, setActiveRole] = useState("admin");
  const [billingAnnual, setBillingAnnual] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileOpen(false);
  };

  return (
    <>
      <style>{floatStyles}</style>
      <div className="min-h-screen bg-white font-body overflow-x-hidden">
        <Navbar
          scrolled={scrolled}
          onNav={scrollTo}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          navigate={navigate}
        />
        <HeroSection navigate={navigate} />
        <StatsBar />
        <FeaturesSection activeRole={activeRole} setActiveRole={setActiveRole} />
        <AISection />
        <HowItWorks />
        <PricingSection billingAnnual={billingAnnual} setBillingAnnual={setBillingAnnual} navigate={navigate} />
        <TestimonialsSection />
        <CTASection navigate={navigate} />
        <Footer onNav={scrollTo} />
      </div>
    </>
  );
}
